import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../../config';
import { Theme } from '../../ui/theme';
import { GameState } from '../../systems/GameState';
import { OUTFITS_BY_ID } from '../../data/outfits';
import { OutfitIconRenderer } from '../../systems/OutfitIconRenderer';
import { svgStringToTexture } from '../../utils/svgToTexture';
import { Button } from '../../ui/Button';
import { ToastManager } from '../../ui/ToastManager';
import { MiniGameRunner } from '../../systems/MiniGameRunner';
import { AudioManager } from '../../systems/AudioManager';
import type { HairStyle } from '../../types';

const ROUND_MS = 30_000;

function colorDistance(a: string, b: string): number {
  const ar = parseInt(a.slice(1,3), 16), ag = parseInt(a.slice(3,5), 16), ab = parseInt(a.slice(5,7), 16);
  const br = parseInt(b.slice(1,3), 16), bg = parseInt(b.slice(3,5), 16), bb = parseInt(b.slice(5,7), 16);
  return Math.abs(ar-br)+Math.abs(ag-bg)+Math.abs(ab-bb);
}

export class ColorMatchScene extends Phaser.Scene {
  private score = 0;
  private timeLeft = ROUND_MS;
  private timer!: Phaser.Time.TimerEvent;
  private scoreText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private targetRect!: Phaser.GameObjects.Rectangle;
  private targetIconImage: Phaser.GameObjects.Image | null = null;
  private optionContainer: Phaser.GameObjects.Container | null = null;
  private targetColor = '#ffffff';
  private toast!: ToastManager;
  private ownedOutfits: string[] = [];
  private progressBar!: Phaser.GameObjects.Rectangle;

  constructor() { super('ColorMatchScene'); }

  create(): void {
    AudioManager.setScene(this);
    AudioManager.playBgm('minigame');
    this.cameras.main.setBackgroundColor('#fef0f5');
    this.toast = new ToastManager(this);
    this.score = 0;
    this.timeLeft = ROUND_MS;
    this.ownedOutfits = GameState.data.ownedOutfits.map(o => o.outfitId).filter(id => OUTFITS_BY_ID[id]);

    this.add.text(GAME_WIDTH/2, 66, '🎨 컬러 매치', { fontFamily: Theme.font.family, fontSize: '34px', fontStyle: '800', color: Theme.hex.text }).setOrigin(0.5);
    this.add.text(GAME_WIDTH/2, 112, '같은 색의 옷을 빨리 찾으세요!', { fontFamily: Theme.font.family, fontSize: '16px', color: Theme.hex.textMid }).setOrigin(0.5);

    // Score chip
    const scoreChipG = this.add.graphics();
    scoreChipG.fillStyle(Theme.colors.pink.bg, 1);
    scoreChipG.fillRoundedRect(16, 10, 180, 44, 12);
    scoreChipG.lineStyle(1.5, Theme.colors.pink.light, 1);
    scoreChipG.strokeRoundedRect(16, 10, 180, 44, 12);
    this.scoreText = this.add.text(106, 32, '점수: 0', { fontFamily: Theme.font.family, fontSize: '20px', fontStyle: '800', color: Theme.hex.text }).setOrigin(0.5);
    // Timer chip
    const timerChipG = this.add.graphics();
    timerChipG.fillStyle(Theme.colors.pink.bg, 1);
    timerChipG.fillRoundedRect(GAME_WIDTH-196, 10, 180, 44, 12);
    timerChipG.lineStyle(1.5, Theme.colors.pink.light, 1);
    timerChipG.strokeRoundedRect(GAME_WIDTH-196, 10, 180, 44, 12);
    this.timerText = this.add.text(GAME_WIDTH-106, 32, '30s', { fontFamily: Theme.font.family, fontSize: '20px', fontStyle: '800', color: Theme.hex.pinkDark }).setOrigin(0.5);

    // Progress bar
    const barX = 200, barY = 20, barW = GAME_WIDTH - 400;
    this.add.rectangle(barX + barW / 2, barY, barW, 12, 0xdddddd).setOrigin(0.5);
    this.progressBar = this.add.rectangle(barX, barY, barW, 12, 0x44ee88).setOrigin(0, 0.5);

    // Target area — moved down so it doesn't overlap title/instructions
    this.add.text(GAME_WIDTH/2, 148, '이 색과 같은 옷을 골라요!', { fontFamily: Theme.font.family, fontSize: '16px', fontStyle: '700', color: Theme.hex.textMid }).setOrigin(0.5);
    this.targetRect = this.add.rectangle(GAME_WIDTH/2, 270, 200, 180, 0xffffff);
    this.targetRect.setStrokeStyle(4, Theme.colors.text.dark);

    new Button(this, 100, GAME_HEIGHT-50, '← 포기', () => this.end(), { width: 120, height: 48, style: 'secondary' });

    this.timer = this.time.addEvent({ delay: 100, loop: true, callback: () => this.tick() });
    void this.nextRound();
  }

  private tick(): void {
    this.timeLeft -= 100;
    this.timerText.setText(`${Math.ceil(this.timeLeft/1000)}s 남음`);
    const ratio = Math.max(0, this.timeLeft / ROUND_MS);
    const barW = GAME_WIDTH - 400;
    this.progressBar.setDisplaySize(barW * ratio, 12);
    const r = Math.min(255, Math.floor((1 - ratio) * 510));
    const g = Math.min(255, Math.floor(ratio * 510));
    this.progressBar.setFillStyle(Phaser.Display.Color.GetColor(r, g, 50));
    if (this.timeLeft <= 0) this.end();
  }

  private async nextRound(): Promise<void> {
    if (this.optionContainer) this.optionContainer.destroy();
    if (this.targetIconImage) { this.targetIconImage.destroy(); this.targetIconImage = null; }
    this.optionContainer = this.add.container(0,0);

    // pick 1 random outfit as target, 3 distractors
    const candidates = this.ownedOutfits.length >= 4
      ? this.ownedOutfits
      : Object.keys(OUTFITS_BY_ID);
    const shuffled = [...candidates].sort(() => Math.random() - 0.5).slice(0, 4);
    const target = shuffled[Math.floor(Math.random() * shuffled.length)];
    this.targetColor = OUTFITS_BY_ID[target].color;
    this.targetRect.setFillStyle(parseInt(this.targetColor.slice(1), 16));

    // Show target outfit icon
    const tOutfit = OUTFITS_BY_ID[target];
    const tHairStyle = (tOutfit.slot === 'hair' && tOutfit.hairStyle) ? tOutfit.hairStyle as HairStyle : undefined;
    const tIconSvg = OutfitIconRenderer.render(tOutfit.slot, tOutfit.color, tHairStyle, tOutfit.iconStyle);
    try {
      await svgStringToTexture(this, `cm_target_${target}`, tIconSvg);
      if (!this.scene.isActive()) return;
      this.targetIconImage = this.add.image(GAME_WIDTH/2, 270, `cm_target_${target}`).setScale(4);
    } catch { /* ignore */ }

    // Options
    await Promise.all(shuffled.map(async (id, i) => {
      const x = 280 + i * 240;
      const y = 540;
      const o = OUTFITS_BY_ID[id];

      // Card
      const card = this.add.graphics();
      card.fillStyle(parseInt(o.color.slice(1), 16), 0.2);
      card.lineStyle(3, parseInt(o.color.slice(1), 16));
      card.fillRoundedRect(x - 80, y - 80, 160, 160, 10);
      card.strokeRoundedRect(x - 80, y - 80, 160, 160, 10);
      this.optionContainer!.add(card);

      // Icon
      const hairStyle = (o.slot === 'hair' && o.hairStyle) ? o.hairStyle as HairStyle : undefined;
      const iconSvg = OutfitIconRenderer.render(o.slot, o.color, hairStyle, o.iconStyle);
      try {
        await svgStringToTexture(this, `cm_opt_${id}`, iconSvg);
        if (!this.scene.isActive() || !this.optionContainer) return;
        const icon = this.add.image(x, y - 20, `cm_opt_${id}`).setScale(3.5);
        this.optionContainer!.add(icon);
      } catch { /* ignore */ }

      const label = this.add.text(x, y + 65, o.name, { fontFamily: Theme.font.family, fontSize: '13px', fontStyle: '700', color: Theme.hex.text }).setOrigin(0.5);
      this.optionContainer!.add(label);

      const hit = this.add.rectangle(x, y, 160, 160, 0x000000, 0);
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => this.pick(id));
      this.optionContainer!.add(hit);
    }));
  }

  private pick(outfitId: string): void {
    const picked = OUTFITS_BY_ID[outfitId].color;
    if (colorDistance(picked, this.targetColor) === 0) {
      this.score += 20;
      AudioManager.playSfx('correct');
      this.toast.show('🎉 정답! +20', 'gold');
    } else {
      this.score = Math.max(0, this.score - 5);
      AudioManager.playSfx('wrong');
      this.toast.show('❌ 다음!');
    }
    this.scoreText.setText(`점수: ${this.score}`);
    void 0;
    void this.nextRound();
  }

  private end(): void {
    this.timer.remove();
    const reward = MiniGameRunner.reportResult('colorMatch', this.score);
    this.cameras.main.fadeOut(200);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.toast.show(`🎉 ${this.score}점! ⭐+${reward.stars}, 💰+${reward.coins}`, 'gold');
      this.time.delayedCall(1800, () => this.scene.start('MiniGameHubScene'));
    });
  }
}
